import * as React from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from '@mui/material'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { useAppTheme } from '@/theme/ThemeContext'

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: React.ReactNode
  content: React.ReactNode
  busy?: boolean
}

const DANGER = '#E53935'

const ConfirmDeleteDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  busy = false,
}) => {
  const { mode } = useAppTheme()
  const isDark = mode === 'dark'
  const colors = {
    bg: isDark ? '#202B40' : '#FFFFFF',
    title: isDark ? '#BBD7FF' : '#003F61',
    content: isDark ? '#BBD7FFCC' : '#003F61',
    border: isDark ? '#4B6FB1' : '#BBD7FF',
    iconBg: isDark ? 'rgba(229,57,53,0.18)' : '#FDECEA',
    cancelBg: isDark ? '#0A3C8B' : '#72A4EB',
    cancelHover: isDark ? '#083070' : '#5F95E3',
    deleteHoverBg: isDark ? 'rgba(229,57,53,0.12)' : 'rgba(229,57,53,0.06)',
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      dir="rtl"
      aria-labelledby="confirm-delete-title"
      aria-describedby="confirm-delete-content"
      slotProps={{
        paper: {
          sx: {
            m: 2,
            width: 390,
            maxWidth: 'calc(100% - 32px)',
            p: 3,
            bgcolor: colors.bg,
            backgroundImage: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            boxShadow: '0px 10px 30px rgba(50, 59, 119, 0.12)',
            textAlign: 'center',
          },
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          mx: 'auto',
          mb: 1.5,
          borderRadius: '50%',
          bgcolor: colors.iconBg,
          color: DANGER,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DeleteOutlineRoundedIcon sx={{ fontSize: 22 }} />
      </Box>
      <Typography
        id="confirm-delete-title"
        component="h2"
        sx={{
          color: colors.title,
          fontWeight: 500,
          mb: 1,
          fontSize: 20,
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        {title}
      </Typography>
      <Typography
        id="confirm-delete-content"
        sx={{
          color: colors.content,
          fontWeight: 300,
          mb: 2.5,
          fontSize: 14,
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        {content}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="outlined"
          startIcon={
            busy ? <CircularProgress size={14} color="inherit" /> : undefined
          }
          sx={{
            flex: 1,
            color: DANGER,
            borderColor: DANGER,
            borderRadius: '4px',
            fontFamily: 'Rubik, sans-serif',
            textTransform: 'none',
            '&:hover': { borderColor: DANGER, bgcolor: colors.deleteHoverBg },
          }}
        >
          מחיקה
        </Button>
        <Button
          onClick={onClose}
          disabled={busy}
          variant="contained"
          disableElevation
          sx={{
            flex: 1,
            bgcolor: colors.cancelBg,
            color: '#FFFFFF',
            borderRadius: '4px',
            fontFamily: 'Rubik, sans-serif',
            textTransform: 'none',
            '&:hover': { bgcolor: colors.cancelHover },
          }}
        >
          ביטול
        </Button>
      </Box>
    </Dialog>
  )
}

export default ConfirmDeleteDialog
